import { useEffect, useMemo, useCallback, type ComponentProps } from "react";
import { type DetailWorkCassette } from "@userFrontend/features/publication/components/DetailWorkCassette";
import { useEntryLink } from "@userFrontend/hooks/useEntryLink";
import { useSWRData } from "@root/shared/hooks/useSWRData";
import { sendErrorLog } from "@root/shared/utils/sendErrorLog";
import { omitEmploymentName } from "@root/userFrontend/utils/formatSalary";
import { type Cassette } from "./type";

export const useRecommendWorksSection = (
  fetchUrl: string,
  gaActionPrefix: string,
  inView: boolean,
): ComponentProps<typeof DetailWorkCassette>[] | undefined => {
  const { handleClickEntryLink } = useEntryLink();
  const { data, mutate } = useSWRData<{ data: Cassette[] }>({
    url: fetchUrl,
    options: {
      revalidateIfStale: true,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      onError: (error: Error) => {
        sendErrorLog(new Error(error.message || "求人データを取得できませんでした"));
      },
    },
  });

  useEffect(() => {
    // NOTE:
    // キープ押下後レコメンド(AfterKeepRecommendWorks)がすでに表示されている状態で、
    // レコメンド求人(当コンポーネント)が画面内に入った場合、
    // 両者のキープボタンの見た目を一致させる目的で、再フェッチを走らせる。
    // 求人詳細以外では、再フェッチを走らせる必要がないため、この関数の呼び出し側からfalseを渡す。
    if (document.getElementById("after-keep-recommend-works-component") && inView) {
      mutate();
    }
  }, [inView, mutate]);

  const createData = useCallback(
    (cassettes: Cassette[]): ComponentProps<typeof DetailWorkCassette>[] => {
      return cassettes.map((item: Cassette) => ({
        gaAction: `${gaActionPrefix}_recommend`,
        gaLabel: item.gaLabel,
        detailUrl: item.linkHref,
        employmentAndSalaryType: item.salaryTexts.map((salary) => ({
          employmentName: omitEmploymentName(salary.employmentTypeName),
          salaryMin: salary.minPrice,
          salaryMinUnit: salary.minUnit,
          salaryMax: salary.maxPrice,
          salaryMaxUnit: salary.maxUnit,
          salaryTypeName: salary.salaryTypeName,
        })),
        imgSrc: item.imgSrc,
        shopLocation: item.shopLocation,
        shopOrBrandName: item.shopOrBrandName,
        businessTypeAndOccupation: item.businessTypeAndOccupation,
        title: item.title,
        buttonProps: {
          workCode: item.workCode,
          hasKeeped: item.keepButtonContent.hasKeeped,
          keepButtonUrl: item.keepButtonContent.keepButtonUrl,
          keepGaAction: `${gaActionPrefix}_recommend_keep`,
          keepGaLabel: item.gaLabel,
          entryUrl: item.entryUrl,
          onClickEntryLink: handleClickEntryLink,
          entryGaAction: `${gaActionPrefix}_recommend_entry`,
          entryGaLabel: item.gaLabel,
        },
      }));
    },
    [gaActionPrefix, handleClickEntryLink],
  );

  const cassettes = useMemo(() => (data?.data ? createData(data.data) : []), [data, createData]);

  return cassettes.length ? cassettes : undefined;
};
